/**
 * @file Labs detail-view fields, controls, and save behavior.
 */

import { appData, state } from "../../app/state/state.js";
import { canAccessView } from "../../app/routing/boardAccess.js";
import {
  renderAuditTrail,
  renderLongTextDetailSection,
} from "../../shared/details/detailSections.js";
import {
  clearDetailDirtyField,
  hasDetailDirtyFields,
} from "../../shared/details/detailDirtyState.js";
import {
  LINKED_PARENT_ACTIONS,
} from "../../shared/details/linkedParentSection.js";
import {
  applyParentCascadingClosure,
  applyParentCascadingHold,
  getParentClosePreflight,
  getParentHoldPreflight,
} from "../../shared/api/parentClosePreflight.js";
import {
  openParentClosePreflightModal,
} from "../../shared/details/parentClosePreflight.js";
import {
  createDateControl,
} from "../../shared/ui/controls/dateControl.js";
import {
  createSelectControl,
} from "../../shared/ui/controls/selectControl.js";
import {
  mergeRegisteredControlValues,
  updateControlValue,
} from "../../shared/ui/controls/controlCore.js";
import {
  createPrioritySelectPreset,
  createProviderSelectPreset,
  createStreamScopedStatusSelectPreset,
  createStreamScopedTypeSelectPreset,
  createUserSelectPreset,
} from "../../shared/ui/controls/controlPresets.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import {
  DETAIL_TITLE_INPUT_CLASSES,
  getNativeFieldWrapperHtml,
  validateNativeFormFields,
} from "../../shared/forms/nativeFieldStyles.js";
import { formatCurrency } from "../../shared/utils/displayUtils.js";
import {
  sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
  resolveItemClosedFromStatus,
} from "../../shared/utils/closedState.js";
import {
  saveLabItem,
  updateLabPatientLink,
} from "./labsApi.js";
import {
  attachLabsPatientLinkFieldListeners,
  getLabsPatientDisplay,
  openLabsPatientLookupModal,
} from "./labsPatientLookupModal.js";
import {
  openLabsPrintReport,
} from "./labsPrintReport.js";
import {
  openLabsCloseModal,
  shouldOpenLabsCloseModal,
} from "./labsCloseModal.js";

const INTERNAL_STREAM = "internal";
const EXTERNAL_STREAM = "external";
const INTERNAL_LAB_ID = "internal";
const DEFAULT_INTERNAL_LAB_LABEL = "Internal lab";
const LIVE_SAVE_FIELDS = Object.freeze([
  "assigned_users",
  "priority",
  "provider",
  "status",
  "types",
]);

function attrValue(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function getLabsStream(item = {}) {
  return item?.stream === INTERNAL_STREAM ? INTERNAL_STREAM : EXTERNAL_STREAM;
}

function getInternalLabLabel() {
  return String(appData?.config?.labs?.internalLabLabel || "").trim() ||
    DEFAULT_INTERNAL_LAB_LABEL;
}

function scalarControlRootHtml(fieldName) {
  return `<div class="min-w-0" data-labs-detail-control="${fieldName}"></div>`;
}

function userCanOpenPatients() {
  return canAccessView(state.currentUserProfile, "patients");
}

function renderPatientHeaderContext(item) {
  const display = getLabsPatientDisplay(item);
  const hasLink = Boolean(display.id);
  if (!hasLink) {
    return `
      <div class="hidden" data-linked-parent-field="labs-linked-patient">
        <input type="hidden" name="linked_patient_id" value="">
      </div>
    `;
  }

  const canOpen = hasLink && userCanOpenPatients();
  const label = display.name || "Linked patient";
  const textClass = canOpen ?
    "task-parent-header-link inline cursor-pointer border-0 bg-transparent p-0 text-left text-[15px] font-medium text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus:text-[var(--dfc-brand-primary)]" :
    "inline-block text-[15px] font-medium text-gray-400";
  const contextText = canOpen ? `
    <button
      type="button"
      class="${textClass}"
      data-linked-parent-action="${LINKED_PARENT_ACTIONS.OPEN}"
      data-linked-parent-field="labs-linked-patient"
      data-linked-parent-id="${attrValue(display.id)}"
      data-linked-parent-type="patient"
    >Patient: ${sanitizeHTML(label)}</button>
  ` : `
    <span class="${textClass}">Patient: ${sanitizeHTML(label)}</span>
  `;

  return `
    <div class="mb-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1" data-linked-parent-field="labs-linked-patient">
      <input type="hidden" name="linked_patient_id" value="${attrValue(display.id || "")}">
      ${contextText}
      <span class="inline-flex items-center gap-0.5">
        ${renderLinkActionButton({
          action: LINKED_PARENT_ACTIONS.LINK,
          icon: "sync_alt",
          label: "Change linked patient",
          patientId: display.id || "",
        })}
        ${renderLinkActionButton({
          action: LINKED_PARENT_ACTIONS.UNLINK,
          icon: "link_off",
          label: "Unlink patient",
          patientId: display.id,
          variant: "danger",
        })}
      </span>
    </div>
  `;
}

function renderPatientTitle(item) {
  const display = getLabsPatientDisplay(item);
  const value = display.id ? display.name : item?.patient_name || "";
  const readOnlyAttrs = display.id ?
    ' readonly aria-readonly="true" title="Patient name is managed by the linked Patient record."' :
    "";

  return `
    <textarea
      name="patient_name"
      class="${DETAIL_TITLE_INPUT_CLASSES} resize-none overflow-hidden"
      placeholder="Patient Name"
      aria-label="Patient Name"
      autocomplete="off"
      rows="1"
      data-auto-grow="true"
      required
      ${readOnlyAttrs}
    >${attrValue(value)}</textarea>
  `;
}

function getActiveExternalLabOptions(selectedLabId = "") {
  const selectedValue = String(selectedLabId || "");
  return (Array.isArray(appData?.config?.labs?.externalLabs) ?
    appData.config.labs.externalLabs :
    [])
    .filter((entry) => {
      return entry?.id &&
        (entry.isActive !== false || String(entry.id) === selectedValue);
    })
    .map((entry) => ({
      value: String(entry.id),
      label: String(entry.name || entry.id),
      order: Number.isFinite(Number(entry.order)) ? Number(entry.order) : 9999,
      archived: entry.isActive === false,
      selectedOnly: entry.isActive === false,
      unavailableLabel: entry.isActive === false ? "archived" : "",
    }))
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

function renderLabField(item, stream) {
  if (stream === INTERNAL_STREAM) {
    return getNativeFieldWrapperHtml("Lab", `
      <input type="hidden" name="lab" value="${INTERNAL_LAB_ID}">
      <div class="rounded-md border border-gray-200 bg-gray-50 px-2 py-2 text-base text-gray-700 sm:min-h-[30px] sm:py-1 sm:text-sm">
        ${sanitizeHTML(getInternalLabLabel())}
      </div>
    `);
  }

  return getNativeFieldWrapperHtml("Lab", scalarControlRootHtml("lab"), {
    required: true,
  });
}

function renderCostField(item, stream) {
  if (stream !== EXTERNAL_STREAM) {
    return `
      <input type="hidden" name="cost" value="">
      <div class="hidden md:block" aria-hidden="true"></div>
    `;
  }

  return getNativeFieldWrapperHtml("Cost", `
    <input
      type="text"
      name="cost"
      value="${attrValue(formatCurrency(item?.cost) || "")}"
      class="native-backed-field min-h-10 w-full rounded-md border border-gray-200 bg-white px-2 py-2 text-base text-gray-900 transition-colors placeholder:text-gray-400 hover:border-[var(--dfc-brand-primary)] focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)] sm:min-h-[30px] sm:py-1 sm:text-sm currency-input"
      placeholder="$"
      inputmode="decimal"
    >
  `);
}

function renderPrintAction(item) {
  return `
    <button
      type="button"
      class="inline-flex h-10 w-fit items-center justify-center rounded-full p-0 text-gray-500 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)] dark:text-gray-400"
      data-labs-print-action
      data-lab-id="${attrValue(item?.id || "")}"
      title="Open Lab sheet"
      aria-label="Open Lab sheet"
    >
      <span class="material-symbols-outlined text-[22px] leading-none" aria-hidden="true">assignment</span>
    </button>
  `;
}

function renderUnlinkedPatientAction(item) {
  if (item?.linked_patient_id) {return "";}
  return `
    <button
      type="button"
      class="mr-1 inline-flex h-10 w-fit items-center justify-center rounded-full p-0 text-gray-500 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)] dark:text-gray-400"
      data-linked-parent-action="${attrValue(LINKED_PARENT_ACTIONS.LINK)}"
      data-linked-parent-field="labs-linked-patient"
      data-linked-parent-id=""
      data-linked-parent-type="patient"
      title="Link patient"
      aria-label="Link patient"
    >
      <span class="material-symbols-outlined pointer-events-none text-[22px] leading-none">link_2</span>
    </button>
  `;
}

function renderLinkActionButton({
  action,
  icon,
  label,
  patientId = "",
  variant = "default",
}) {
  const classes = variant === "danger" ?
    "inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-red-600 focus:outline-none focus:text-red-600" :
    "inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus:text-[var(--dfc-brand-primary)]";

  return `
    <button
      type="button"
      class="${classes}"
      data-linked-parent-action="${attrValue(action)}"
      data-linked-parent-field="labs-linked-patient"
      data-linked-parent-id="${attrValue(patientId)}"
      data-linked-parent-type="patient"
      title="${attrValue(label)}"
      aria-label="${attrValue(label)}"
    >
      <span class="material-symbols-outlined pointer-events-none text-base leading-none">${icon}</span>
    </button>
  `;
}

/**
 * Generates HTML for editable Labs detail fields.
 * @param {object} item Lab item data.
 * @returns {string} Labs detail fields HTML.
 */
export function getLabsDetailFields(item = {}) {
  const stream = getLabsStream(item);

  return `
    <div class="bg-white pt-6 px-6 pb-3 rounded-lg shadow-md mb-6">
      <input type="hidden" name="stream" value="${attrValue(stream)}">
      <div class="mb-4 pb-2">
        ${renderPatientHeaderContext(item)}
        <div class="flex min-w-0 items-start justify-between gap-3">
          <div class="min-w-0 flex-1">${renderPatientTitle(item)}</div>
          <div class="flex shrink-0 items-center gap-2">
            ${renderUnlinkedPatientAction(item)}
            ${renderPrintAction(item)}
          </div>
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-6 text-sm">
        ${getNativeFieldWrapperHtml("Type(s)", scalarControlRootHtml("types"), {
          required: true,
        })}
        ${getNativeFieldWrapperHtml("Provider", scalarControlRootHtml("provider"), {
          required: true,
        })}
        ${renderLabField(item, stream)}
        ${getNativeFieldWrapperHtml("Assigned Users", scalarControlRootHtml("assigned_users"), {
          required: true,
        })}
        ${getNativeFieldWrapperHtml("Urgency", scalarControlRootHtml("priority"))}
        ${getNativeFieldWrapperHtml("Status", scalarControlRootHtml("status"))}
        ${getNativeFieldWrapperHtml("Due Date", scalarControlRootHtml("due_date"))}
        ${renderCostField(item, stream)}
        ${renderLongTextDetailSection({
          fieldName: "details",
          label: "Details",
          value: item?.details || "",
          wrapperClass: "md:col-span-4",
        })}
      </div>
      ${renderAuditTrail(item)}
    </div>
  `;
}

function getControlRoot(form, fieldName) {
  return form?.querySelector(`[data-labs-detail-control="${fieldName}"]`);
}

/**
 * Mounts registered controls for Labs details.
 * @param {HTMLElement} detailsViewContainer Active detail view container.
 * @param {object} item Lab item data.
 * @returns {object} Mounted controls keyed by field name.
 */
export function setupLabsDetailControls(detailsViewContainer, item = {}) {
  const controls = {};
  const form = detailsViewContainer?.querySelector("#details-form");
  if (!form) {return controls;}

  const stream = getLabsStream(item);

  const typeRoot = getControlRoot(form, "types");
  if (typeRoot) {
    controls.types = createStreamScopedTypeSelectPreset({
      root: typeRoot,
      appData,
      itemType: "labs",
      stream,
      value: Array.isArray(item?.types) ? item.types : [],
      required: true,
      requiredMessage: "Choose a Lab type.",
      scopeUser: state.currentUserProfile,
      searchable: true,
    });
  }

  const priorityRoot = getControlRoot(form, "priority");
  if (priorityRoot) {
    controls.priority = createPrioritySelectPreset({
      root: priorityRoot,
      appData,
      value: item?.priority || "",
      clearable: false,
    });
  }

  const statusRoot = getControlRoot(form, "status");
  if (statusRoot) {
    controls.status = createStreamScopedStatusSelectPreset({
      root: statusRoot,
      appData,
      itemType: "labs",
      stream,
      value: item?.status || "",
      clearable: false,
    });
  }

  const providerRoot = getControlRoot(form, "provider");
  if (providerRoot) {
    controls.provider = createProviderSelectPreset({
      root: providerRoot,
      appData,
      value: Array.isArray(item?.provider) ? item.provider : [],
      required: true,
      searchable: true,
    });
  }

  const assignedUsersRoot = getControlRoot(form, "assigned_users");
  if (assignedUsersRoot) {
    controls.assigned_users = createUserSelectPreset({
      root: assignedUsersRoot,
      appData,
      name: "assigned_users",
      collectionName: "labs",
      value: Array.isArray(item?.assigned_users) ? item.assigned_users : [],
      multiple: true,
      required: true,
      searchable: true,
    });
  }

  const dueDateRoot = getControlRoot(form, "due_date");
  if (dueDateRoot) {
    controls.due_date = createDateControl({
      root: dueDateRoot,
      name: "due_date",
      type: "date",
      value: item?.due_date || "",
    });
  }

  const labRoot = getControlRoot(form, "lab");
  if (labRoot && stream === EXTERNAL_STREAM) {
    controls.lab = createSelectControl({
      root: labRoot,
      name: "lab",
      value: item?.lab || "",
      options: getActiveExternalLabOptions(item?.lab),
      placeholder: "+ Add external lab",
      clearable: false,
      required: true,
      requiredMessage: "Choose an external lab.",
    });
  }

  return controls;
}

function normalizeCurrencyInput(value) {
  if (value === null || value === undefined || value === "") {return null;}
  const number = typeof value === "number" ?
    value :
    Number(String(value).replace(/[$,\s]/g, ""));
  return Number.isFinite(number) ? number : null;
}

function getLabsCloseCandidate(item = {}, detailsForm = null, targetStatusId = "") {
  return {
    ...item,
    ...(detailsForm ? getFormPayload(detailsForm, item) : {}),
    status: targetStatusId || item?.status || "",
  };
}

function isLabsOnHoldStatus(statusId) {
  return (appData?.config?.labStatus || [])
    .some((status) => status?.id === statusId && status?.isOnHold === true);
}

function getFormPayload(form, item = {}) {
  const formData = new FormData(form);
  const payload = {};
  for (const [key, value] of formData.entries()) {
    if (key === "undefined" || key.includes("form-")) {
      continue;
    }
    payload[key] = typeof value === "string" ? value : value;
  }
  const merged = mergeRegisteredControlValues(payload, form);
  merged.stream = getLabsStream(item);
  merged.linked_patient_id = item?.linked_patient_id || null;
  if (Object.prototype.hasOwnProperty.call(merged, "cost")) {
    merged.cost = getLabsStream(item) === EXTERNAL_STREAM ?
      normalizeCurrencyInput(merged.cost) :
      null;
  }
  if (getLabsStream(item) === INTERNAL_STREAM) {
    merged.lab = INTERNAL_LAB_ID;
  }
  merged.due_date = merged.due_date || null;
  return merged;
}

function resetSaveButtonsIfNoManualDirtyFields(detailsForm) {
  if (!detailsForm || hasDetailDirtyFields(detailsForm)) {return;}
  state.hasUnsavedChanges = false;
  const root = detailsForm.closest("#details-view") || document;
  root.querySelectorAll(".details-save-btn").forEach((button) => {
    button.disabled = true;
    button.classList.remove(
      "bg-[var(--dfc-brand-primary)]",
      "hover:bg-[var(--dfc-brand-primary-hover)]",
      "shadow-md",
      "text-gray-900",
    );
    button.classList.add("bg-gray-200", "hover:bg-gray-300", "text-gray-800");
  });
}

function clearLiveSaveDirtyState(detailsForm, fieldName) {
  clearDetailDirtyField(fieldName);
  resetSaveButtonsIfNoManualDirtyFields(detailsForm);
}

function applySavedLabResult(item, payload) {
  if (!item) {return;}
  Object.assign(item, payload);
}

function applySaveResponseToLab(item, response = {}) {
  if (!item || !response?.updatedAt) {return;}
  item.updatedAt = response.updatedAt;
}

function applyLocalLabCloseResult(item, targetStatusId) {
  if (!item) {return;}
  item.status = targetStatusId;
  item.isClosedResolved = true;
  state.hasUnsavedChanges = false;
}

function applyLocalLabHoldResult(item, targetStatusId) {
  if (!item) {return;}
  item.status = targetStatusId;
  item.isClosedResolved = false;
  state.hasUnsavedChanges = false;
}

function rollbackStatusControl({
  controlId,
  item,
  previousStatus,
  renderDetailsView,
  updateSelectBackgrounds,
}) {
  if (item) {
    item.status = previousStatus || "";
  }
  if (controlId) {
    updateControlValue(controlId, previousStatus || "", {silent: true});
    updateSelectBackgrounds?.();
    return;
  }
  renderDetailsView?.();
  updateSelectBackgrounds?.();
}

function getLabSaveFailureMessage(result) {
  const blockerMessage = (result?.blockers || [])
    .map((blocker) => blocker?.message)
    .filter(Boolean)
    .join(" ");
  return blockerMessage || result?.message || "Lab Item could not be saved.";
}

async function persistLabPayload({
  item,
  payload,
}) {
  const result = await saveLabItem({
    itemId: item.id,
    data: payload,
  });

  if (result?.success === false) {
    throw new Error(getLabSaveFailureMessage(result));
  }

  applySavedLabResult(item, payload);
  applySaveResponseToLab(item, result);
  return true;
}

async function applyLabsClose({
  cascade = {},
  completionData = {},
  detailsForm,
  item,
  labCompletionDataById = {},
  renderDetailsView,
  selections = {},
  targetStatusId,
  updateSelectBackgrounds,
}) {
  const response = await applyParentCascadingClosure({
    cascade,
    completionData,
    itemId: item.id,
    itemType: "lab",
    labCompletionDataById,
    selections,
    targetStatusId,
  });
  if (response?.success === false) {
    throw new Error(getLabSaveFailureMessage(response));
  }
  applyLocalLabCloseResult(item, targetStatusId);
  if (Object.prototype.hasOwnProperty.call(completionData, "cost")) {
    item.cost = normalizeCurrencyInput(completionData.cost);
  }
  renderDetailsView?.();
  const activeForm = document.querySelector("#details-form") || detailsForm;
  clearLiveSaveDirtyState(activeForm, "status");
  updateSelectBackgrounds?.();
  return response;
}

async function applyLabsHold({
  cascade = {},
  detailsForm,
  item,
  renderDetailsView,
  selections = {},
  targetStatusId,
  updateSelectBackgrounds,
}) {
  const response = await applyParentCascadingHold({
    cascade,
    itemId: item.id,
    itemType: "lab",
    selections,
    targetStatusId,
  });
  if (response?.success === false) {
    throw new Error(getLabSaveFailureMessage(response));
  }
  applyLocalLabHoldResult(item, targetStatusId);
  renderDetailsView?.();
  const activeForm = document.querySelector("#details-form") || detailsForm;
  clearLiveSaveDirtyState(activeForm, "status");
  updateSelectBackgrounds?.();
  return response;
}

async function handleLabsHoldStatusChange({
  controlId,
  detailsForm,
  item,
  previousStatus,
  renderDetailsView,
  targetStatusId,
  updateSelectBackgrounds,
}) {
  if (!isLabsOnHoldStatus(targetStatusId)) {
    return false;
  }

  const rollback = () => rollbackStatusControl({
    controlId,
    item,
    previousStatus,
    renderDetailsView,
    updateSelectBackgrounds,
  });

  try {
    const preflight = await getParentHoldPreflight({
      itemId: item.id,
      itemType: "lab",
      targetStatusId,
    });
    const finishHold = async ({cascade = {}, selections = {}} = {}) => {
      try {
        return await applyLabsHold({
          cascade,
          detailsForm,
          item,
          renderDetailsView,
          selections,
          targetStatusId,
          updateSelectBackgrounds,
        });
      } catch (error) {
        rollback();
        throw error;
      }
    };

    if (preflight?.shouldIntercept) {
      openParentClosePreflightModal({
        preflight,
        onCancel: rollback,
        onApply: finishHold,
        onSkip: finishHold,
      });
      return true;
    }

    await finishHold();
    return true;
  } catch (error) {
    rollback();
    openConfirmationModal(
      "Lab Item Not Updated",
      sanitizeHTML(error?.message || "Lab Item could not be put on hold."),
      async () => {},
      false,
      "OK",
    );
    return true;
  }
}

async function handleLabsCloseStatusChange({
  controlId,
  detailsForm,
  item,
  previousStatus,
  renderDetailsView,
  targetStatusId,
  updateSelectBackgrounds,
}) {
  if (!resolveItemClosedFromStatus("lab", targetStatusId)) {
    return false;
  }

  const rollback = () => rollbackStatusControl({
    controlId,
    item,
    previousStatus,
    renderDetailsView,
    updateSelectBackgrounds,
  });

  const submitClose = async (completionData = {}, options = {}) => {
    const preflight = await getParentClosePreflight({
      itemId: item.id,
      itemType: "lab",
      targetStatusId,
    });
    const finishClose = async ({
      cascade = {},
      labCompletionDataById = {},
      selections = {},
    } = {}) => {
      try {
        return await applyLabsClose({
          cascade,
          labCompletionDataById,
          completionData,
          detailsForm,
          item,
          renderDetailsView,
          selections,
          targetStatusId,
          updateSelectBackgrounds,
        });
      } catch (error) {
        rollback();
        throw error;
      }
    };

    if (preflight?.shouldIntercept) {
      const openPreflight = () => openParentClosePreflightModal({
        preflight,
        onCancel: rollback,
        onApply: finishClose,
        onSkip: finishClose,
      });
      if (options.deferPreflightModal) {
        window.setTimeout(openPreflight, 0);
      } else {
        openPreflight();
      }
      return true;
    }

    await finishClose();
    return true;
  };

  try {
    const closeCandidate = getLabsCloseCandidate(
        item,
        detailsForm,
        targetStatusId,
    );
    if (shouldOpenLabsCloseModal({
      item: closeCandidate,
      config: appData?.config || {},
    })) {
      openLabsCloseModal({
        item: closeCandidate,
        onCancel: rollback,
        onSubmit: (completionData) => submitClose(
            completionData,
            {deferPreflightModal: true},
        ),
      });
      return true;
    }

    await submitClose({
      cost: closeCandidate.cost,
    });
    return true;
  } catch (error) {
    rollback();
    openConfirmationModal(
      "Lab Item Not Closed",
      sanitizeHTML(error?.message || "Lab Item could not be closed."),
      async () => {},
      false,
      "OK",
    );
    return true;
  }
}

async function saveLabsLiveField({
  controlId,
  detailsForm,
  fieldName,
  item,
  renderDetailsView,
  updateSelectBackgrounds,
  value,
}) {
  const previousValue = item?.[fieldName];
  const payload = {[fieldName]: value};

  const requiredUserListLabels = {
    assigned_users: "Assigned User",
    provider: "Provider",
  };
  const requiredUserListLabel = requiredUserListLabels[fieldName];

  if (requiredUserListLabel && (!Array.isArray(value) || value.length === 0)) {
    if (controlId) {
      updateControlValue(controlId, previousValue, {silent: true});
    }
    openConfirmationModal(
      `${requiredUserListLabel} Required`,
      `Choose at least one ${requiredUserListLabel} before saving.`,
      async () => {},
      false,
      "OK",
    );
    renderDetailsView?.();
    updateSelectBackgrounds?.();
    return false;
  }

  try {
    if (fieldName === "status") {
      const handledClose = await handleLabsCloseStatusChange({
        controlId,
        detailsForm,
        item,
        previousStatus: previousValue,
        renderDetailsView,
        targetStatusId: value,
        updateSelectBackgrounds,
      });
      if (handledClose) {return true;}
      const handledHold = await handleLabsHoldStatusChange({
        controlId,
        detailsForm,
        item,
        previousStatus: previousValue,
        renderDetailsView,
        targetStatusId: value,
        updateSelectBackgrounds,
      });
      if (handledHold) {return true;}
    }
    await persistLabPayload({item, payload});
    clearLiveSaveDirtyState(detailsForm, fieldName);
    updateSelectBackgrounds?.();
    return true;
  } catch (error) {
    console.error("Failed to save Labs detail field:", error);
    if (controlId) {
      updateControlValue(controlId, previousValue, {silent: true});
    }
    openConfirmationModal(
      "Lab Item Not Saved",
      sanitizeHTML(error?.message || "Failed to save Lab Item detail field."),
      async () => {},
      false,
      "OK",
    );
    renderDetailsView?.();
    updateSelectBackgrounds?.();
    return false;
  }
}

/**
 * Saves the Labs detail form through backend Labs validation.
 * @param {object} params Save params.
 * @param {HTMLFormElement} params.detailsForm Detail form.
 * @param {object} params.item Lab item.
 * @returns {Promise<boolean>} True when save succeeds.
 */
export async function saveLabsDetailsForm({detailsForm, item}) {
  if (!detailsForm || !item?.id) {return false;}
  if (!validateNativeFormFields(detailsForm)) {return false;}

  try {
    const payload = getFormPayload(detailsForm, item);
    await persistLabPayload({item, payload});
    state.hasUnsavedChanges = false;
    return true;
  } catch (error) {
    console.error("Failed to save Labs details:", error);
    openConfirmationModal(
      "Lab Item Not Saved",
      sanitizeHTML(error?.message || "Lab Item could not be saved."),
      async () => {},
      false,
      "OK",
    );
    return false;
  }
}

function getLinkFailureMessage(response) {
  const blockerMessages = (response?.blockers || [])
    .map((blocker) => blocker?.message)
    .filter(Boolean);
  if (blockerMessages.length > 0) {
    return blockerMessages.join(" ");
  }
  if (response?.reason === "stale_state") {
    return "This Lab Item changed since the action was opened. Refresh and try again.";
  }
  if (response?.reason === "patient_closed") {
    return "Closed patients cannot be linked to Lab Items.";
  }
  return "The linked Patient could not be updated.";
}

function applyPatientLinkResult(item, response, fallbackPatient = null) {
  if (!item || response?.changed === false) {return;}
  const current = response?.current || {};
  item.linked_patient_id = current.patientId || fallbackPatient?.id || null;
  item.patient_name = current.patientName || fallbackPatient?.name ||
    item.patient_name || "";
  item.case_file_number =
    current.case_file_number || fallbackPatient?.case_file_number || "";
  item.card_id = current.card_id || fallbackPatient?.card_id || "";
  item.updatedAt = current.updatedAt || item.updatedAt || null;
  item.linked_patient_types = Array.isArray(current.types) ?
    current.types :
    (Array.isArray(fallbackPatient?.types) ? fallbackPatient.types : []);
}

async function updateDetailPatientLink({item, patientId, patient = null}) {
  const response = await updateLabPatientLink({
    labId: item.id,
    patientId,
    expectedUpdatedAt: item.updatedAt || null,
  });
  if (response?.success === false) {
    throw new Error(getLinkFailureMessage(response));
  }
  applyPatientLinkResult(item, response, patient);
  return response;
}

function attachLabsPatientLinkListeners(detailsViewContainer, item, context) {
  attachLabsPatientLinkFieldListeners(detailsViewContainer, {
    onLink: () => {
      openLabsPatientLookupModal({
        selectedPatientId: item?.linked_patient_id || "",
        selectedPatient: getLabsPatientDisplay(item),
        onSelect: async (patient) => {
          await updateDetailPatientLink({
            item,
            patient,
            patientId: patient?.id || null,
          });
          context.renderDetailsView?.();
          return true;
        },
      });
    },
    onOpen: ({targetId}) => {
      if (!targetId || !userCanOpenPatients()) {return;}
      context.showDetails?.(targetId, "patient");
    },
    onUnlink: () => {
      if (!item?.linked_patient_id) {return;}
      openConfirmationModal(
        "Clear Patient Link",
        "Are you sure you want to unlink this Lab Item from its assigned Patient? You should only unlink the item if the linking is incorrect.",
        async () => {
          try {
            await updateDetailPatientLink({
              item,
              patientId: null,
            });
            context.renderDetailsView?.();
          } catch (error) {
            openConfirmationModal(
              "Patient Link Not Updated",
              sanitizeHTML(error?.message || "The linked Patient could not be cleared."),
              async () => {},
              false,
              "OK",
            );
          }
        },
        true,
        "Clear Link",
        null,
        "Clearing...",
      );
    },
  });
}

function attachLabsPrintListeners(detailsViewContainer, item) {
  detailsViewContainer
    .querySelectorAll("[data-labs-print-action]")
    .forEach((button) => {
      button.onclick = () => {
        openLabsPrintReport(item || button.dataset.labId || "");
      };
    });
}

/**
 * Detail adapter for Labs parent items.
 */
export const labDetailAdapter = {
  itemType: "lab",
  viewName: "labs",
  statusConfigKey: "labStatus",
  typeConfigKey: "labTypes",
  liveSaveFields: LIVE_SAVE_FIELDS,
  showManualSavePendingState: true,
  validateEmptyTypesOnClear: true,
  sections: [
    "linkedTasks",
    "checklist",
    "notes",
    "attachments",
  ],
  renderFields: getLabsDetailFields,
  mountControls(detailsViewContainer, item) {
    setupLabsDetailControls(detailsViewContainer, item);
  },
  handleControlValueChange({
    event,
    item,
    renderDetailsView,
    updateSelectBackgrounds,
    value,
  }) {
    const fieldName = event.detail?.name || "";
    if (!LIVE_SAVE_FIELDS.includes(fieldName)) {return false;}
    const detailsForm = event.target?.closest?.("#details-form") || null;
    void saveLabsLiveField({
      controlId: event.detail?.id || "",
      detailsForm,
      fieldName,
      item,
      renderDetailsView,
      updateSelectBackgrounds,
      value,
    });
    return true;
  },
  saveRegisteredDetailValue({
    controlId,
    detailsForm,
    fieldName,
    item,
    renderDetailsView,
    updateSelectBackgrounds,
    value,
  }) {
    return saveLabsLiveField({
      controlId,
      detailsForm,
      fieldName,
      item,
      renderDetailsView,
      updateSelectBackgrounds,
      value,
    });
  },
  async saveManualDetails({detailsForm, item}) {
    return saveLabsDetailsForm({detailsForm, item});
  },
  attachPostFormListeners(detailsViewContainer, item, context) {
    attachLabsPatientLinkListeners(detailsViewContainer, item, context);
    attachLabsPrintListeners(detailsViewContainer, item);
  },
  getBackNavigation() {
    return {type: "view", view: "labs"};
  },
  getDeleteNavigation() {
    return {type: "view", view: "labs"};
  },
};

/**
 * Returns whether Labs detail routes are currently exposed.
 * @returns {boolean} True now that Labs detail behavior is implemented.
 */
export function isLabsDetailRuntimeEnabled() {
  return true;
}
